/**
 * Keeps one WebSocket per ticker for the whole stock view — survives child remounts
 * and pushes price updates without a page refresh.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getLivePriceWebSocketUrl } from '@/services/api';
import type { LivePriceMessage } from '@/hooks/useMassiveLivePrice';

export type LivePriceState = {
  livePrice: number | null;
  connected: boolean;
  streaming: boolean;
  error: string | null;
  notice: string | null;
  feedMode: 'realtime' | 'delayed' | null;
  lastUpdatedAt: number | null;
};

const defaultState: LivePriceState = {
  livePrice: null,
  connected: false,
  streaming: false,
  error: null,
  notice: null,
  feedMode: null,
  lastUpdatedAt: null,
};

const LivePriceContext = createContext<LivePriceState>(defaultState);

type ProviderProps = {
  symbol: string;
  enabled: boolean;
  children: ReactNode;
};

const RECONNECT_MS = 2000;

export function LivePriceProvider({ symbol, enabled, children }: ProviderProps) {
  const [state, setState] = useState<LivePriceState>(defaultState);
  const wsRef = useRef<WebSocket | null>(null);
  const symbolRef = useRef(symbol);

  const wsEnabled = enabled;

  const applyPrice = useCallback((price: number, source?: string) => {
    setState((prev) => ({
      ...prev,
      livePrice: price,
      error: null,
      lastUpdatedAt: Date.now(),
      streaming: source === 'stream' ? true : prev.streaming,
      connected: true,
    }));
  }, []);

  useEffect(() => {
    symbolRef.current = symbol;

    if (!wsEnabled || !symbol.trim()) {
      if (wsRef.current) {
        wsRef.current.close(1000, 'disabled');
        wsRef.current = null;
      }
      setState(defaultState);
      return;
    }

    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const sym = symbol.trim().toUpperCase();

    const attach = (ws: WebSocket) => {
      ws.onopen = () => {
        if (cancelled || wsRef.current !== ws) return;
        setState((prev) => ({ ...prev, connected: true, error: null }));
      };

      ws.onmessage = (event) => {
        if (cancelled || wsRef.current !== ws) return;
        try {
          const data = JSON.parse(event.data as string) as LivePriceMessage;
          if (data.type === 'price' && typeof data.price === 'number') {
            applyPrice(data.price, data.source);
            if (data.source === 'stream' || data.source === 'poll') {
              setState((prev) => ({ ...prev, streaming: true }));
            }
          } else if (data.type === 'connected') {
            const feed =
              (data as { feed?: string }).feed === 'realtime' ? 'realtime' : 'delayed';
            setState((prev) => ({
              ...prev,
              connected: true,
              error: null,
              feedMode: feed,
            }));
          } else if (data.type === 'notice') {
            setState((prev) => ({
              ...prev,
              notice: (data as { message?: string }).message ?? null,
              feedMode: 'delayed',
            }));
          } else if (data.type === 'error') {
            setState((prev) => ({
              ...prev,
              error: data.message,
              connected: false,
              streaming: false,
            }));
          }
        } catch {
          // ignore
        }
      };

      ws.onerror = () => {
        if (cancelled || wsRef.current !== ws) return;
        setState((prev) => ({
          ...prev,
          error: 'Live price connection failed',
          connected: false,
          streaming: false,
        }));
      };

      ws.onclose = (ev) => {
        if (cancelled || wsRef.current !== ws) return;
        setState((prev) => ({
          ...prev,
          connected: false,
          streaming: false,
        }));
        if (ev.code !== 1000 && !cancelled) {
          reconnectTimer = setTimeout(connect, RECONNECT_MS);
        }
      };
    };

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(getLivePriceWebSocketUrl(sym));
      wsRef.current = ws;
      attach(ws);
    };

    setState((prev) => ({
      ...prev,
      error: null,
      streaming: false,
    }));
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      wsRef.current?.close(1000, 'unmount');
      wsRef.current = null;
    };
  }, [symbol, wsEnabled, applyPrice]);

  const value = useMemo(() => state, [state]);

  return (
    <LivePriceContext.Provider value={value}>{children}</LivePriceContext.Provider>
  );
}

export function useLivePrice(): LivePriceState {
  return useContext(LivePriceContext);
}
